import { VertexRun, VertexRunBucket } from "@/http/api-types";

// Canonical state order for stacking/legend; unknown states are appended after these.
const STATE_ORDER = ["FAILED", "CANCELLED", "RUNNING", "SUCCEEDED"];

export const VERTEX_RUN_STATE_COLORS: Record<string, string> = {
  FAILED: "hsl(0 72% 51%)",
  CANCELLED: "hsl(38 92% 50%)",
  RUNNING: "hsl(217 91% 60%)",
  SUCCEEDED: "hsl(142 71% 45%)",
};

export const VERTEX_RUN_FALLBACK_COLOR = "hsl(215 16% 47%)";

export type VertexRunsChartRow = { date: string } & Record<string, number | string>;

function toUTCDate(value: string): string | null {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
}

function addUTCDay(date: string): string {
  const next = new Date(`${date}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString().slice(0, 10);
}

function orderStates(states: Set<string>): string[] {
  const known = STATE_ORDER.filter(state => states.has(state));
  const extra = Array.from(states)
    .filter(state => !STATE_ORDER.includes(state))
    .sort();
  return [...known, ...extra];
}

/**
 * buildVertexRunsChartData turns the per-state run buckets into per-day counts for a stacked bar
 * chart. Each run is placed on its create_time day (UTC). Gap days between the first and last run
 * are filled with zero rows so the timeline reads continuously.
 */
export function buildVertexRunsChartData(buckets: VertexRunBucket[]): {
  rows: VertexRunsChartRow[];
  states: string[];
} {
  const statesPresent = new Set<string>();
  const countsByDate = new Map<string, Record<string, number>>();

  for (const bucket of buckets) {
    for (const run of bucket.runs) {
      const day = toUTCDate(run.create_time);
      if (!day) continue;
      statesPresent.add(bucket.state);
      const counts = countsByDate.get(day) ?? {};
      counts[bucket.state] = (counts[bucket.state] ?? 0) + 1;
      countsByDate.set(day, counts);
    }
  }

  const states = orderStates(statesPresent);
  const days = Array.from(countsByDate.keys()).sort();
  if (days.length === 0) {
    return { rows: [], states };
  }

  const rows: VertexRunsChartRow[] = [];
  for (let day = days[0]; day <= days[days.length - 1]; day = addUTCDay(day)) {
    const counts = countsByDate.get(day) ?? {};
    const row: VertexRunsChartRow = { date: day };
    for (const state of states) {
      row[state] = counts[state] ?? 0;
    }
    rows.push(row);
  }

  return { rows, states };
}

export function vertexRunStateColor(state: string): string {
  return VERTEX_RUN_STATE_COLORS[state] ?? VERTEX_RUN_FALLBACK_COLOR;
}

export type VertexRunsModelGroup = {
  model: string;
  runs: VertexRun[];
};

/**
 * groupRunsByModel flattens the per-state buckets and regroups the runs by model name. Groups are
 * sorted by model; within a group runs are ordered newest-first by create_time.
 */
export function groupRunsByModel(buckets: VertexRunBucket[]): VertexRunsModelGroup[] {
  const byModel = new Map<string, VertexRun[]>();
  for (const bucket of buckets) {
    for (const run of bucket.runs) {
      const runs = byModel.get(run.model) ?? [];
      runs.push(run);
      byModel.set(run.model, runs);
    }
  }

  return Array.from(byModel.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([model, runs]) => ({
      model,
      runs: runs.sort((a, b) => new Date(b.create_time).getTime() - new Date(a.create_time).getTime()),
    }));
}
